import React from "react";

function cx(...parts: Array<string | false | null | undefined>) {
  return parts.filter(Boolean).join(" ");
}

export interface AuthShellFeature {
  icon: React.ReactNode;
  title: string;
  description: string;
}

export interface AuthShellFormSlotProps {
  actions: React.ReactNode;
}

export interface AuthShellProps {
  appName: string;
  appDescription: string;
  title: string;
  subtitle: string;
  footerNote?: string;
  footerAction?: React.ReactNode;
  supportMeta?: React.ReactNode;
  topBar?: React.ReactNode;
  homeAction?: React.ReactNode;
  brandMark?: React.ReactNode;
  brandActionLabel?: string;
  onBrandClick?: () => void;
  features: AuthShellFeature[];
  form: (props: AuthShellFormSlotProps) => React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
}

export const AuthShell: React.FC<AuthShellProps> = ({
  appName,
  appDescription,
  title,
  subtitle,
  footerNote,
  footerAction,
  supportMeta,
  topBar,
  homeAction,
  brandMark,
  brandActionLabel,
  onBrandClick,
  features,
  form,
  actions,
  className,
}) => {
  const defaultActions = (
    <div className="flex items-center gap-2 rounded-full border border-border-subtle bg-white/82 px-2 py-2 shadow-sm backdrop-blur dark:bg-[rgba(15,23,28,0.78)] dark:shadow-[0_10px_30px_rgba(0,0,0,0.28)]">
      {topBar}
    </div>
  );
  const brandContent = (
    <>
      <div className="flex h-24 w-24 items-center justify-center rounded-2xl bg-white/20 shadow-[0_12px_24px_-4px_rgba(0,0,0,0.15)] backdrop-blur-md">
        {brandMark}
      </div>
      <div className="flex flex-col gap-2">
        <h1 className="text-4xl font-extrabold tracking-[-0.02em]">
          {appName}
        </h1>
        <p className="text-[15px] text-white/85">{appDescription}</p>
      </div>
    </>
  );
  const mobileBrandContent = (
    <>
      <div className="brand-gradient mb-4 flex h-16 w-16 items-center justify-center rounded-2xl shadow-[0_16px_32px_rgba(15,125,116,0.24)]">
        {brandMark}
      </div>
      <h1 className="text-3xl font-bold tracking-[-0.03em]">{appName}</h1>
      <p className="mt-2 text-sm text-text-muted">{appDescription}</p>
    </>
  );

  return (
    <div
      className={cx(
        "relative min-h-screen overflow-hidden bg-bg-page text-text-primary",
        className,
      )}
    >
      <div className="absolute right-4 top-4 z-20 sm:right-6 sm:top-6">
        {topBar ? defaultActions : null}
      </div>
      {homeAction ? (
        <div className="absolute left-4 top-4 z-20 sm:left-6 sm:top-6">
          {homeAction}
        </div>
      ) : null}

      <div className="grid min-h-screen lg:grid-cols-2">
        <section className="brand-gradient relative hidden overflow-hidden lg:flex">
          <div className="relative z-10 flex w-full flex-col items-center justify-center gap-12 px-16 py-20">
            {onBrandClick ? (
              <button
                type="button"
                onClick={onBrandClick}
                className="flex max-w-md flex-col items-center gap-6 rounded-2xl text-center text-white transition-transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80"
                aria-label={brandActionLabel}
              >
                {brandContent}
              </button>
            ) : (
              <div className="flex max-w-md flex-col items-center gap-6 text-center text-white">
                {brandContent}
              </div>
            )}

            <div className="flex w-full max-w-[480px] flex-col gap-7">
              {features.map((feature) => (
                <div
                  key={feature.title}
                  className="flex items-center gap-5 rounded-xl bg-white/[0.08] px-5 py-4 text-white"
                >
                  <div className="flex shrink-0 items-center justify-center text-[28px] leading-none">
                    {feature.icon}
                  </div>
                  <div className="flex flex-col gap-1">
                    <h2 className="text-[17px] font-bold">{feature.title}</h2>
                    <p className="text-sm leading-relaxed text-white/85">
                      {feature.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="pointer-events-none absolute inset-0 opacity-[0.08]">
            <div className="absolute left-[-7%] top-[6%] h-[300px] w-[300px] rounded-full bg-white opacity-40" />
            <div className="absolute left-[53%] top-[33%] h-[250px] w-[250px] rounded-full bg-white opacity-50" />
            <div className="absolute left-[69%] top-[72%] h-[280px] w-[280px] rounded-full bg-white opacity-30" />
            <div className="absolute left-[21%] top-[78%] h-[200px] w-[200px] rounded-full bg-white opacity-[0.35]" />
          </div>
        </section>

        <section className="relative flex min-h-screen items-center justify-center bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.9),rgba(249,250,251,1)_42%)] px-4 py-20 dark:bg-[radial-gradient(circle_at_top,rgba(36,53,62,0.3),rgba(13,20,23,1)_44%)] sm:px-6 lg:px-16">
          <div className="w-full max-w-[400px]">
            <div className="mb-8 flex flex-col items-center text-center text-text-primary lg:hidden">
              {onBrandClick ? (
                <button
                  type="button"
                  onClick={onBrandClick}
                  className="flex flex-col items-center rounded-2xl transition-transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-action-primary"
                  aria-label={brandActionLabel}
                >
                  {mobileBrandContent}
                </button>
              ) : (
                mobileBrandContent
              )}
            </div>

            <div className="rounded-2xl border border-border-subtle bg-bg-surface p-8 shadow-[0_4px_24px_rgba(0,0,0,0.08)] dark:bg-[rgba(23,34,40,0.92)] dark:shadow-[0_18px_44px_rgba(0,0,0,0.34)] sm:p-10">
              <header className="mb-8">
                <h2 className="text-[28px] font-bold tracking-[-0.03em] text-text-primary">
                  {title}
                </h2>
                <p className="mt-2 text-sm text-text-muted">{subtitle}</p>
              </header>

              {form({ actions })}
            </div>

            {(supportMeta || footerNote || footerAction) && (
              <div className="mt-8 flex flex-wrap items-center justify-center gap-2 text-center text-xs text-text-muted">
                {supportMeta}
                {supportMeta && (footerNote || footerAction) ? <span>·</span> : null}
                {footerNote ? <span>{footerNote}</span> : null}
                {footerNote && footerAction ? <span>·</span> : null}
                {footerAction ? (
                  <span className="text-primary-ink">{footerAction}</span>
                ) : null}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};
